/**
 * Keeping the document's named text styles in a real stylesheet.
 *
 * A `<span>` cannot carry an inline `style`: `domain/sanitise.ts` strips it on
 * purpose, since a stored `style` attribute reaches a stylesheet with no
 * chance to check it first. A `class` can be checked instead, which is what a
 * text style writes into the document — so something has to turn the
 * catalogue of styles into the actual CSS those classes name. This does that
 * once, for the whole application, in a `<style>` element it owns.
 *
 * A real element in `document.head` rather than a `v-bind`-computed style
 * block: PDF and HTML export both read every rule out of
 * `document.styleSheets` to build a self-contained page (see
 * `ExportDialog.vue`), and a plain stylesheet is all that path understands.
 */
import { onBeforeUnmount, onMounted, watchEffect } from 'vue'

import { textStyleSheet } from '@/domain/layout/textStyles'
import { useReportStore } from '@/stores/report'

export function useTextStyleSheet(): void {
  const report = useReportStore()
  const element = document.createElement('style')
  element.setAttribute('data-text-styles', '')

  onMounted(() => document.head.appendChild(element))
  onBeforeUnmount(() => element.remove())

  watchEffect(() => {
    element.textContent = textStyleSheet(report.layout.textStyles, report.fonts)
  })
}
